import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import host from "../../global";
import { ROUNDS } from "../../constants/secondaryCamera";
import {
  CONSENT_ITEMS_CODING,
  PROCTORING_INSTRUCTIONS_CODING,
} from "../../constants/interviewInstructions";
import PreInterviewCheck from "./PreInterviewCheck";

// Rules the candidate sees on the Ready step, carried over verbatim from the
// old standalone "Coding Challenge" screen this flow used to end on.
const CODING_RULES = [
  "The test consists of multiple coding problems.",
  "You may use any programming language of your choice, but ensure your code is executable in the provided environment.",
  "Make sure to test your code thoroughly before submission.",
  "Do not attempt to access external websites or resources. The use of unauthorized resources will be monitored.",
  "The test will automatically submit when the time is up, so manage your time wisely.",
];

// Apex (Salesforce) rounds are reviewed by AI instead of being executed, so the
// standard "executable in the provided environment" rules would be misleading.
const APEX_RULES = [
  "The test consists of Salesforce Apex coding questions.",
  "You will write Apex in the editor. Your code is NOT executed or compiled; it is reviewed by AI against a rubric.",
  "Write production-quality code: bulk-safe, no SOQL or DML inside loops, handle null and empty input, and follow Salesforce best practices.",
  "You can edit and re-submit an answer until you end the test.",
  "Do not attempt to access external websites or resources. The use of unauthorized resources will be monitored.",
  "The test will automatically submit when the time is up, so manage your time wisely.",
];

// The coding-challenge flavour of the shared pre-check flow.
const CodingInstructions = () => {
  const { interview_id } = useParams();
  const [isApex, setIsApex] = useState(false);

  // Read-only lookup; if it fails the standard rules are shown.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${host}/api/coding_round_info/${interview_id}`)
      .then((res) => { if (!cancelled) setIsApex(res.data?.track === "apex"); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [interview_id]);

  const config = useMemo(
    () => ({
      round: ROUNDS.CODING,
      railTitle: "Pre-test check",
      consentItems: CONSENT_ITEMS_CODING,
      instructions: PROCTORING_INSTRUCTIONS_CODING,

      // No countdown: the old flow handed off immediately, and only went
      // fullscreen when proctoring was on. Both preserved.
      useCountdown: false,
      shouldGoFullscreen: (isVideoProctoringEnabled) =>
        Boolean(isVideoProctoringEnabled),

      // Kept: the Ready step is where this round's rules are shown.
      hasReadyStep: true,

      ready: {
        headline: "Cleared to begin",
        startButtonLabel: "Start Coding",
        checklist: isApex ? APEX_RULES : CODING_RULES,
      },

      proceedErrorMessage:
        "Failed to start the coding challenge. Please try again or contact support.",

      // No greet/thank calls for this round — straight to the test.
      onProceed: async ({ interviewId, navigate }) => {
        navigate(`/candidate/codingtest/${interviewId}`);
      },
    }),
    [isApex]
  );

  return <PreInterviewCheck config={config} />;
};

export default CodingInstructions;
